import React, {useMemo} from 'react';

import {useStore} from 'effector-react';
import {TFunction} from 'i18next';
import {useTranslation} from 'react-i18next';
import {generatePath, Link, useHistory} from 'react-router-dom';

import {Page} from 'components/common/Page';
import {ButtonVariant} from 'components/ui-kit/Button';
import {Input} from 'components/ui-kit/Input';
import {ContentContainer} from 'components/ui-kit/PageComponents';
import {ROUTE} from 'core/routes';
import {permissions$} from 'models/auth';
import {filtersChanged, organizationState$} from 'models/profile/organizations/table';
import {
    OrganizationAccessors as Accessors,
    OrganizationFormattedRow,
    OrganizationRawRow,
} from 'types/profile';
import {handleNil} from 'utils/handleNil';

import {ProfileNavigationTabs} from '../NavigationTabs';
import {Wrapper} from '../styled';

import {
    Cell,
    CreateOrganizationButton,
    HeaderControls,
    SearchWrapper,
    UserStatusTag,
} from './styled';
import {OrganizationGridTable} from './Table';

export function convertData(
    data: OrganizationRawRow[] | undefined,
    t: TFunction,
): Partial<OrganizationFormattedRow>[] {
    if (!data) return [];

    return data.map((elem) => ({
        [Accessors.taxIdNumber]: handleNil(elem.taxIdNumber, (v) => (
            <Link to={generatePath(ROUTE.ORGANIZATIONS_DETAIL, {id: elem.taxIdNumber})}>{v}</Link>
        )),
        [Accessors.legalName]: handleNil(elem.legalName),
        [Accessors.address]: handleNil(elem.address.address),
        [Accessors.email]: handleNil(elem.email, (v) => <Cell>{v}</Cell>),
        [Accessors.phone]: handleNil(elem.phone, (v) => <Cell>{v}</Cell>),
        [Accessors.balance]: handleNil(elem.balance),
        [Accessors.isBlocked]: handleNil(elem, (val: OrganizationRawRow) => {
            const {isBlocked} = val;
            return (
                <UserStatusTag isActive={!isBlocked}>
                    {isBlocked
                        ? t('dictionaries.profile.staff.status.BLOCKED')
                        : t('dictionaries.profile.staff.status.ACTIVE')}
                </UserStatusTag>
            );
        }),
    }));
}

const getConfig = (t: TFunction) => [
    {
        id: Accessors.taxIdNumber,
        header: t('pages.profile.organizations.columns.taxIdNumber'),
        width: 140,
    },
    {
        id: Accessors.legalName,
        header: t('pages.profile.organizations.columns.legalName'),
        maxWidth: 250,
    },
    {
        id: Accessors.address,
        header: t('pages.profile.organizations.columns.address'),
        width: 300,
    },
    {
        id: Accessors.email,
        header: t('pages.profile.organizations.columns.email'),
        width: 220,
    },
    {
        id: Accessors.phone,
        header: t('pages.profile.organizations.columns.phone'),
        width: 130,
    },
    {
        id: Accessors.balance,
        header: t('pages.profile.organizations.columns.balance'),
        width: 120,
    },
    {
        id: Accessors.isBlocked,
        header: t('pages.profile.organizations.columns.status'),
        width: 90,
    },
];

export const Organizations = () => {
    const {t} = useTranslation();
    const history = useHistory();
    const permissions = useStore(permissions$);
    const {filters} = useStore(organizationState$);
    const searchValue = useMemo(() => {
        const value = filters.find(({id}) => id === Accessors.taxIdNumber)?.value;
        return typeof value === 'string' ? value : '';
    }, [filters]);

    const CreateBtn = () => {
        if (!permissions.includes(permissions.organizationRegister)) return null;

        return (
            <CreateOrganizationButton
                size="sm"
                variant={ButtonVariant.Normal}
                type="button"
                onClick={() => history.push(ROUTE.ORGANIZATIONS_CREATE)}
            >
                + {t('pages.profile.organizations.create.createBtn')}
            </CreateOrganizationButton>
        );
    };

    const headerControls = (
        <HeaderControls>
            <SearchWrapper>
                <Input
                    size="sm"
                    value={searchValue}
                    placeholder={t('pages.profile.organizations.searchByTaxId')}
                    onChange={(event) =>
                        filtersChanged([
                            {
                                id: Accessors.taxIdNumber,
                                value: event.target.value,
                            },
                        ])
                    }
                />
            </SearchWrapper>
            <CreateBtn />
        </HeaderControls>
    );

    return (
        <Page title={t('pages.profile.title')}>
            <Wrapper>
                <ProfileNavigationTabs />
                <ContentContainer noPaddings>
                    <OrganizationGridTable
                        configFunc={getConfig}
                        dataMapper={convertData}
                        headerControls={headerControls}
                    />
                </ContentContainer>
            </Wrapper>
        </Page>
    );
};
